import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, XCircle, Sparkles, ChevronDown, ChevronUp } from "lucide-react";
import { PROTECTION_PRODUCTS } from "../data/protectionProducts";
import ProtectionDeclineModal from "./ProtectionDeclineModal";

/**
 * Optional Coverages step on /scotts-deal-desk.
 * v6.5.2 — Renamed from "Optional Protection Products" → "Optional Coverages".
 *
 * Layout:
 *  • "Recommended for You" — fixed 4 cards (GAP, Vehicle Service Contract,
 *    Paint & Fabric Defense, 5-in-1 Combo). Always visible.
 *  • "All Optional Coverages" — collapsed by default. Toggled via the
 *    "Show All Optional Coverages" / "Hide Optional Coverages" button.
 *
 * Selections lifted via onChange(payload) so the parent BYOD page can include
 * `selected` (coverages), `declined`, and decline-modal interactions in the
 * final lead submission AND so the PaymentCalculator can include the selected
 * coverages total in Amount Financed.
 */

// v6.5.2 — fixed recommended set per Scott's spec.
const RECOMMENDED_SLUGS = [
  "gap-protection",
  "vehicle-service-contract",
  "paint-fabric-defense",
  "5-in-1-combo",
];

// Why-this-was-recommended copy per Scott's spec.
const WHY_BY_SLUG = {
  "gap-protection": "On a 72-month loan, the gap between value and payoff can stay open for years.",
  "vehicle-service-contract": "Longer terms keep you in the vehicle past the factory warranty — coverage may help.",
  "paint-fabric-defense": "Paint & Fabric Defense works best when applied early, before daily wear, weather, spills, and road grime have time to settle in.",
  "5-in-1-combo": "A bundled plan can make sense when you want broader day-to-day ownership protection in one package instead of choosing separate coverages.",
};

export default function ProtectionStep({ initialFocusSlug, onChange, recommendationContext }) {
  // Map slug -> { status: "added"|"declined"|null, finalPrice, acceptedSecondChance }
  const [states, setStates] = useState({});
  const [modalSlug, setModalSlug] = useState(null);
  const [interactions, setInteractions] = useState([]);
  const [showAll, setShowAll] = useState(false);

  // Recommended cards always include the fixed 4 slugs.
  const recommendedSet = useMemo(() => new Set(RECOMMENDED_SLUGS), []);
  const recommendedProducts = useMemo(
    () => RECOMMENDED_SLUGS.map((s) => PROTECTION_PRODUCTS.find((p) => p.slug === s)).filter(Boolean),
    []
  );
  const restProducts = useMemo(
    () => PROTECTION_PRODUCTS.filter((p) => !recommendedSet.has(p.slug)),
    [recommendedSet]
  );

  // Subtext mentions the payment term if available.
  const term = Number(recommendationContext?.paymentTerm || 0);
  const recommendedSubtext = term >= 60
    ? `Because you're considering a ${term}-month term, these may be especially helpful — but they're still entirely optional.`
    : "These coverages tend to be the most relevant for most buyers — but they're still entirely optional.";

  // Honor ?protection=<slug> deep-link from coverage landing pages
  useEffect(() => {
    if (!initialFocusSlug) return;
    // If the deep-linked coverage is in the rest list, auto-expand so scroll works.
    if (!recommendedSet.has(initialFocusSlug)) setShowAll(true);
    const el = document.getElementById(`protection-card-${initialFocusSlug}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [initialFocusSlug, recommendedSet]);

  // Notify parent on state change
  useEffect(() => {
    const selected = Object.entries(states)
      .filter(([, s]) => s.status === "added")
      .map(([slug, s]) => {
        const p = PROTECTION_PRODUCTS.find((x) => x.slug === slug);
        return {
          slug,
          name: p?.name || slug,
          price: s.finalPrice ?? p?.price ?? null,
          acceptedSecondChance: !!s.acceptedSecondChance,
        };
      });
    const declined = Object.entries(states)
      .filter(([, s]) => s.status === "declined")
      .map(([slug]) => slug);
    onChange?.({ selected, declined, interactions });
  }, [states, interactions, onChange]);

  const logInteraction = (slug, action) => {
    setInteractions((arr) => [...arr, { slug, action, at: new Date().toISOString() }]);
  };

  const handleAddClick = (product) => {
    setStates((s) => ({
      ...s,
      [product.slug]: { status: "added", finalPrice: product.price ?? null, acceptedSecondChance: false },
    }));
  };

  const handleDeclineClick = (product) => {
    logInteraction(product.slug, "decline-opened");
    setModalSlug(product.slug);
  };

  const handleModalAdd = ({ slug, acceptedSecondChance, finalPrice }) => {
    if (acceptedSecondChance) logInteraction(slug, "second-chance-accepted");
    setStates((s) => ({ ...s, [slug]: { status: "added", finalPrice, acceptedSecondChance } }));
  };

  const handleModalKeepDeclined = (slug) => {
    setStates((s) => ({ ...s, [slug]: { status: "declined", finalPrice: null, acceptedSecondChance: false } }));
  };

  const modalProduct = PROTECTION_PRODUCTS.find((p) => p.slug === modalSlug) || null;

  return (
    <div className="space-y-5" data-testid="byod-protection-step">
      <p className="text-[var(--ssc-ink-2)]">
        These coverages are optional. They are designed to help protect your vehicle, your budget,
        or both. Choose any coverages you want Scott to include in your deal review, or decline anything you do not want.
      </p>

      {/* ====== RECOMMENDED FOR YOU ====== */}
      <section data-testid="protection-recommended-section">
        <div className="bg-[var(--ssc-blue)]/5 border border-[var(--ssc-blue)]/20 rounded-2xl p-4 mb-4">
          <div className="flex items-start gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-[var(--ssc-blue)] text-white grid place-items-center shrink-0">
              <Sparkles size={16} />
            </div>
            <div>
              <div className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--ssc-blue)]">
                Recommended for You
              </div>
              <p className="text-sm text-[var(--ssc-ink)] mt-1 leading-snug" data-testid="protection-recommended-reason">
                {recommendedSubtext}
              </p>
            </div>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          {recommendedProducts.map((product) => (
            <ProductCard
              key={product.slug}
              product={product}
              state={states[product.slug] || {}}
              isRecommended
              why={WHY_BY_SLUG[product.slug]}
              onAddClick={handleAddClick}
              onDeclineClick={handleDeclineClick}
              onRemove={(slug) => setStates((s) => ({ ...s, [slug]: {} }))}
              onLearnMore={(slug) => logInteraction(slug, "learn-more")}
            />
          ))}
        </div>
      </section>

      {/* ====== ALL OPTIONAL COVERAGES (collapsed by default) ====== */}
      <section data-testid="protection-all-section">
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="w-full flex items-center justify-between gap-3 rounded-xl border border-[var(--ssc-line)] bg-white px-4 py-3 text-left hover:border-[var(--ssc-blue)] transition"
          data-testid="protection-toggle-all"
          aria-expanded={showAll}
        >
          <span className="flex items-center gap-2">
            <span className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--ssc-ink-2)]">
              {showAll ? "Hide Optional Coverages" : "Show All Optional Coverages"}
            </span>
            <span className="text-[10px] font-bold text-[var(--ssc-ink-2)]">({restProducts.length} more)</span>
          </span>
          {showAll
            ? <ChevronUp size={18} className="text-[var(--ssc-ink-2)]" />
            : <ChevronDown size={18} className="text-[var(--ssc-ink-2)]" />}
        </button>

        {showAll && (
          <div className="mt-4 grid sm:grid-cols-2 gap-4" data-testid="protection-all-list">
            {restProducts.map((product) => (
              <ProductCard
                key={product.slug}
                product={product}
                state={states[product.slug] || {}}
                onAddClick={handleAddClick}
                onDeclineClick={handleDeclineClick}
                onRemove={(slug) => setStates((s) => ({ ...s, [slug]: {} }))}
                onLearnMore={(slug) => logInteraction(slug, "learn-more")}
              />
            ))}
          </div>
        )}
      </section>

      <ProtectionDeclineModal
        product={modalProduct}
        onClose={() => setModalSlug(null)}
        onAdd={handleModalAdd}
        onKeepDeclined={handleModalKeepDeclined}
        onLearnMore={(slug) => window.open(`/protection-products/${slug}`, "_blank")}
      />

      {/* ====== SCOTT'S NOTE (trust signal — consultative, not pushy) ====== */}
      <ScottNoteCard />
    </div>
  );
}

function ScottNoteCard() {
  return (
    <section
      className="mt-2 bg-[var(--ssc-navy)] text-white rounded-2xl p-5 sm:p-6"
      data-testid="protection-scott-note"
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-full bg-[var(--ssc-blue)] grid place-items-center text-white shrink-0 font-black">
          SH
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-extrabold uppercase tracking-[0.25em] text-[var(--ssc-silver)]">
            A quick note from Scott
          </div>
          <p className="mt-2 text-sm sm:text-base text-white leading-relaxed">
            Some folks add everything, some folks only add GAP, and plenty of folks
            pass on it all — every one of those is a fine answer. I'll happily walk you
            through the trade-offs and won't push you toward anything you don't want.
            Text or call me anytime and we'll figure out what actually makes sense for
            how long you'll keep the vehicle and how you drive.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <a
              href="sms:+16145358673"
              className="ssc-btn ssc-btn-primary text-sm"
              data-testid="protection-scott-text"
            >
              Text Scott — (614) 535-8673
            </a>
            <a
              href="tel:+16145358673"
              className="ssc-btn ssc-btn-outline-white text-sm"
              data-testid="protection-scott-call"
            >
              Call Scott
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Card component — reused across both sections.
// Shows price only when the coverage has one. Otherwise hides price entirely
// (no invented numbers — "See details" lives on the Learn More page).
// ---------------------------------------------------------------------------
function ProductCard({ product, state, isRecommended, why, onAddClick, onDeclineClick, onRemove, onLearnMore }) {
  const isAdded = state.status === "added";
  const isDeclined = state.status === "declined";
  const hasPrice = Number.isFinite(product.price) && product.price > 0;

  return (
    <div
      id={`protection-card-${product.slug}`}
      className={`relative bg-white border-2 rounded-2xl p-4 flex flex-col transition ${
        isAdded ? "border-emerald-500 bg-emerald-50/50"
        : isDeclined ? "border-[var(--ssc-line)] opacity-70"
        : isRecommended ? "border-[var(--ssc-blue)]/40 shadow-sm"
        : "border-[var(--ssc-line)]"
      }`}
      data-testid={`protection-card-${product.slug}`}
    >
      {isRecommended && (
        <div className="absolute -top-2.5 left-3 flex items-center gap-1 bg-[var(--ssc-blue)] text-white text-[9px] font-extrabold uppercase tracking-widest px-2 py-1 rounded-full shadow"
             data-testid={`protection-recommended-badge-${product.slug}`}>
          <Sparkles size={10} /> Recommended
        </div>
      )}

      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${
          isAdded ? "bg-emerald-500 text-white" : "bg-[var(--ssc-blue)]/10 text-[var(--ssc-blue)]"
        }`}>
          <product.Icon size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-extrabold text-[var(--ssc-navy)] leading-tight">{product.name}</div>
          {hasPrice && (
            <div className="text-sm font-black text-[var(--ssc-navy)] mt-0.5"
                 data-testid={`protection-price-${product.slug}`}>
              ${state.finalPrice ? state.finalPrice.toLocaleString() : product.price.toLocaleString()}
              {state.acceptedSecondChance && state.finalPrice < product.price && (
                <span className="ml-2 text-xs font-bold text-emerald-700">Second-chance price</span>
              )}
            </div>
          )}
          <p className="text-xs text-[var(--ssc-ink-2)] mt-1.5 leading-snug">
            {product.shortDesc}
          </p>
        </div>
      </div>

      {/* "Why" rationale */}
      {isRecommended && why && (
        <div className="mt-3 bg-[var(--ssc-blue)]/5 rounded-lg px-3 py-2 border border-[var(--ssc-blue)]/15"
             data-testid={`protection-why-${product.slug}`}>
          <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--ssc-blue)] mr-1">Why:</span>
          <span className="text-xs text-[var(--ssc-ink)]">{why}</span>
        </div>
      )}

      {isAdded && (
        <div className="mt-3 flex items-center gap-1.5 text-xs font-bold text-emerald-700"
             data-testid={`protection-status-added-${product.slug}`}>
          <CheckCircle2 size={14} /> Added to your deal.
        </div>
      )}
      {isDeclined && (
        <div className="mt-3 flex items-center gap-1.5 text-xs font-bold text-[var(--ssc-ink-2)]"
             data-testid={`protection-status-declined-${product.slug}`}>
          <XCircle size={14} /> Declined — not included.
        </div>
      )}

      <div className="mt-auto pt-3 flex flex-wrap gap-2">
        {!isAdded && (
          <button onClick={() => onAddClick(product)}
                  className="ssc-btn ssc-btn-primary text-xs px-3 py-2"
                  data-testid={`protection-add-${product.slug}`}>
            Add to My Deal
          </button>
        )}
        {isAdded && (
          <button
            onClick={() => onRemove(product.slug)}
            className="ssc-btn ssc-btn-outline text-xs px-3 py-2"
            data-testid={`protection-remove-${product.slug}`}
          >
            Remove
          </button>
        )}
        {!isDeclined && !isAdded && (
          <button onClick={() => onDeclineClick(product)}
                  className="ssc-btn ssc-btn-outline text-xs px-3 py-2"
                  data-testid={`protection-decline-${product.slug}`}>
            Decline
          </button>
        )}
        <Link
          to={`/protection-products/${product.slug}`}
          target="_blank" rel="noopener noreferrer"
          className="text-xs font-bold uppercase tracking-wider text-[var(--ssc-blue)] underline px-2 py-2"
          onClick={() => onLearnMore(product.slug)}
          data-testid={`protection-learn-more-${product.slug}`}
        >
          Learn More →
        </Link>
      </div>
    </div>
  );
}
